// src/pages/Vouchers/VoucherPrintDialog.jsx
import React, { useState, useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, IconButton, Typography, CircularProgress,
} from '@mui/material';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';
import { toast } from 'react-toastify';
import VoucherPrint from './VoucherPrint';

export default function VoucherPrintDialog({ open, onClose, voucher, companyInfo }) {
  const printRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const number = voucher?.voucherNumber || 'جديد';

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `سند-${number}`,
  });

  const handlePdf = async () => {
    if (!printRef.current) return;
    setPdfLoading(true);
    try {
      await html2pdf().set({
        margin: [8, 8, 8, 8],
        filename: `سند-${number}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all'] },
      }).from(printRef.current).save();
    } catch (err) {
      console.error('PDF error:', err);
      toast.error('تعذّر إنشاء ملف PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { direction: 'rtl', borderRadius: '16px', overflow: 'hidden', height: { xs: '100%', sm: '90vh' }, m: { xs: 1, sm: 4 } } }}
    >
      {/* الترويسة */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, color: '#fff',
        background: 'linear-gradient(120deg, #4338ca 0%, #4f46e5 45%, #6366f1 100%)',
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center',
          bgcolor: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.28)',
        }}>
          <ReceiptLongOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>معاينة الطباعة</Typography>
          <Typography sx={{ fontSize: 12.5, opacity: .85 }}>
            {voucher?.voucherTypeName ? `${voucher.voucherTypeName} · ` : ''}رقم {number}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }} aria-label="إغلاق">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* الورقة */}
      <DialogContent sx={{ p: { xs: 1, sm: 2.5 }, bgcolor: '#e5e7eb' }}>
        <Box sx={{
          mx: 'auto', width: '100%', maxWidth: '210mm', bgcolor: '#fff',
          boxShadow: '0 10px 30px -12px rgba(15,23,42,.35)', borderRadius: '4px', overflow: 'hidden',
        }}>
          <VoucherPrint ref={printRef} voucher={voucher} companyInfo={companyInfo} />
        </Box>
      </DialogContent>

      {/* الأزرار */}
      <DialogActions sx={{ px: 2, py: 1.25, gap: 1, borderTop: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        <Button
          variant="contained" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} disabled={!voucher}
          sx={{ minWidth: 120, borderRadius: '11px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}
        >
          طباعة
        </Button>
        <Button
          variant="outlined" onClick={handlePdf} disabled={!voucher || pdfLoading}
          startIcon={pdfLoading ? <CircularProgress size={16} /> : <PictureAsPdfOutlinedIcon />}
          sx={{ minWidth: 120, borderRadius: '11px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}
        >
          {pdfLoading ? 'جاري التحميل…' : 'تحميل PDF'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} color="inherit" sx={{ borderRadius: '11px', fontWeight: 700 }}>إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
}
